import type { MedplumClient } from '@medplum/core';
import type { Group } from '@medplum/fhirtypes';

import { GROUP_THERAPY_TAG_CODE, GROUP_THERAPY_TAG_SYSTEM } from '@unify/fhir-model/constants';
import {
  clearGroupLifecycleInactiveTag,
  isTherapyCohortGroup,
} from '@unify/fhir-model/resources/group';
import {
  formValuesToGroupResource,
  groupListItemFromResource,
} from './groupFhirMapping';
import { getEpisodeOfCareGroupRef } from './groupEnrollment';
import type { GroupListItem, GroupSetupFormValues } from './types';

const THERAPY_GROUP_TAG = `${GROUP_THERAPY_TAG_SYSTEM}|${GROUP_THERAPY_TAG_CODE}`;

export async function searchTherapyGroups(
  medplum: MedplumClient,
  opts?: { organizationId?: string; activeOnly?: boolean },
): Promise<Group[]> {
  const params: Record<string, string> = {
    _tag: THERAPY_GROUP_TAG,
    _count: '100',
    _sort: '-_lastUpdated',
  };
  if (opts?.organizationId) {
    params['managing-entity'] = `Organization/${opts.organizationId}`;
  }
  // Medplum does not index Group.active — filter client-side after fetch.
  const results = await medplum.searchResources('Group', params);
  const therapyGroups = results.filter(isTherapyCohortGroup);
  if (opts?.activeOnly) {
    return therapyGroups.filter((g) => g.active !== false);
  }
  return therapyGroups;
}

export async function createTherapyGroup(
  medplum: MedplumClient,
  values: GroupSetupFormValues,
): Promise<GroupListItem> {
  const resource = formValuesToGroupResource(values);
  const created = await medplum.createResource(resource);
  medplum.invalidateSearches('Group');
  return groupListItemFromResource(created);
}

export async function updateTherapyGroup(
  medplum: MedplumClient,
  values: GroupSetupFormValues,
  existing: Group,
): Promise<GroupListItem> {
  const resource = formValuesToGroupResource(values, existing);
  const updated = await medplum.updateResource(resource);
  medplum.invalidateSearches('Group');
  return groupListItemFromResource(updated);
}

/**
 * Complete / end a Group cohort (PRD §13):
 * - Set active=false only (no lifecycle inactive tag)
 * - End all active membership periods
 * - Move active Group Therapy EoCs for this Group to onhold
 * - Keep CareTeams and facilitator access (formal discharge is separate)
 */
export async function archiveTherapyGroup(
  medplum: MedplumClient,
  group: Group,
): Promise<GroupListItem> {
  if (!group.id) throw new Error('Group must have an id.');
  const periodEnd = new Date().toISOString().slice(0, 10);

  const endedMembers = (group.member ?? []).map((m) =>
    m.inactive
      ? m
      : {
          ...m,
          inactive: true,
          period: { ...m.period, end: m.period?.end ?? periodEnd },
        },
  );

  const updated = await medplum.updateResource(
    clearGroupLifecycleInactiveTag({
      ...group,
      active: false,
      member: endedMembers,
      quantity: 0,
    }),
  );

  const groupRef = `Group/${group.id}`;
  const episodes = await medplum.searchResources('EpisodeOfCare', {
    status: 'active,planned',
    _count: '200',
  });
  await Promise.all(
    episodes
      .filter((eoc) => getEpisodeOfCareGroupRef(eoc)?.reference === groupRef)
      .map((eoc) => medplum.updateResource({ ...eoc, status: 'onhold' })),
  );

  medplum.invalidateSearches('Group');
  medplum.invalidateSearches('EpisodeOfCare');
  return groupListItemFromResource(updated);
}

export async function readTherapyGroup(
  medplum: MedplumClient,
  groupId: string,
): Promise<Group | undefined> {
  try {
    const group = await medplum.readResource('Group', groupId);
    return isTherapyCohortGroup(group) ? group : undefined;
  } catch {
    return undefined;
  }
}
